import { ObjectManager } from '../../../concepts/objects';
import { RelativeTo } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { ActiveObject, resolve } from '../../Container';
import { Zone } from './Zone';
import { ZonePeers, ZonePeersAttr } from './ZoneAssociations';

export class ZonePeerMechanics extends ActiveObject {

    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(SpatialIndex) private spatial!: SpatialIndex;

    private readonly invalidations = new Set<Zone>();

    public start() {
        this.burden(
            this.objects.on('Zone', 'affected', ([c]) => this.invalidate(c.as(Zone))),
        );
        this.invalidateAll();
        this.update();
    }

    private invalidate = (zone: Zone) => {
        this.invalidations.add(zone);
        this.scheduler.timeout('update', 0, this.update);
    };

    private invalidateAll = () => {
        for (const z of this.objects.getAll(Zone)) {
            this.invalidations.add(z);
        }
    };

    private update = () => {

        const done = new Set<Zone>();
        const queue = Array.from(this.invalidations);

        while (queue.length > 0) {
            const zone = queue.shift();
            if (done.has(zone)) continue;
            if (zone.alive) {
                const peers = this.findPeers(zone);
                const value = peers.map(x => !!x) as unknown as ZonePeers;
                ZonePeersAttr.set(zone, value);
                queue.push(...(peers.filter(x => !!x)));
            }

            done.add(zone);
        }
    };

    private findPeers(zone: Zone): [Zone?, Zone?, Zone?, Zone?] {
        const zb = zone.bounds(RelativeTo.Model).round();
        const candidates = Physicals.polarize(this.spatial.lookup(zb.inflate(5)), Zone);
        const result = zb.lines().map(ze =>
            // For each line edge, if a candidate has a line edge that is equal and it's center is not inside
            // the zone, then it's a peer.
            candidates.find(c => {
                const cb = c.bounds(RelativeTo.Model).round();
                return cb.lines().find(ce => ze.equals(ce, false)) && !cb.contains(zb.center());
            })
        );
        return result as any;
    }
}
